import React, { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import * as yup from 'yup';
import { useFormik } from 'formik';
import styled from 'styled-components';
import moment from 'moment';
import { Modal } from 'antd';
import { ThemeProps } from '../../../../genericComponents/globalStyles';
import headerBackground from '../../../../../assets/images/headerBackgroundYellow.png';
import { CharacterCounter } from '../../../../genericComponents/CharacterCounter';
const maxDescriptionChar = 3000

const schema = yup.object().shape({
  job_title: yup.string().required().max(200, 'Job title must be at most 200 characters'),
  nbr_hours_served: yup
    .number()
    .required()
    .typeError('Number of hours served must be a number')
    .min(0)
    .max(
      2000000,
      'Number of hours served  must be less than or equal to 2000000',
    ),
  city: yup
    .string()
    .required()
    .max(200, 'Organization address must be at most 200 characters'),
  organization_name: yup
    .string()
    .required()
    .max(200, 'Organization name must be at most 200 characters'),
  description: yup
    .string()
    .required()
    .max(maxDescriptionChar, `Description must be at most ${maxDescriptionChar} characters`),
  start_date: yup.date(),
  end_date: yup
    .date()
    .min(yup.ref('start_date'), "End Date can't be before Start Date"),
});

const initialValues = {
  end_date: new Date(),
  start_date: new Date(),
  description: '',
  job_title: '',
  nbr_hours_served: '',
  city: '',
  organization_name: '',
};

export default function VolunteerModal(props) {
  const {
    isShow,
    onHide,
    addNewVolunteer,
    editVolunteer,
    deleteVolunteer,
    editValues,
  } = props;
  const [numberCharacters, setNumberCharacters] = useState(initialValues.description.length);

  useEffect(() => {
    if (isShow) {
      setFieldValue("city", editValues?.city || '')
      setFieldValue("description", editValues?.description || '')
      setFieldValue("job_title", editValues?.job_title || '')
      setFieldValue("nbr_hours_served", editValues?.nbr_hours_served || '')
      setFieldValue("organization_name", editValues?.organization_name || '')
      setFieldValue("start_date", new Date(moment(`01/${editValues?.start_date}`, 'DD/MM/YYYY',).format()))
      setFieldValue("end_date", new Date(moment(`01/${editValues?.end_date}`, 'DD/MM/YYYY',).format()))
      setNumberCharacters(editValues?.description.length || 0)
    }
  }, [isShow])

  const {
    errors,
    handleChange,
    handleSubmit,
    values,
    setFieldValue,
    touched,
    setFieldTouched,
  } = useFormik({
    initialValues: { ...initialValues },
    validationSchema: schema,
    onSubmit: onSubmit,
  });

  function onSubmit(values) {
    const newVolunteer = {
      id: '',
      subcollectionId: '',
      city: values.city,
      description: values.description,
      start_date: `${values.start_date.getMonth() + 1}/${values.start_date.getFullYear()}`,
      end_date: `${values.end_date.getMonth() + 1}/${values.end_date.getFullYear()}`,
      job_title: values.job_title,
      nbr_hours_served: values.nbr_hours_served,
      organization_name: values.organization_name,
      subcollectionName: "volunteers"
    };
    if (editValues) {
      newVolunteer.subcollectionId = editValues.id;
      newVolunteer.id = editValues.id;
      editVolunteer(newVolunteer);
    } else {
      addNewVolunteer(newVolunteer);
    }
  }

  function onDelete(e) {
    e.preventDefault();
    deleteVolunteer({
      subcollectionId: editValues.id,
      subcollectionName: 'volunteers'
    });
  }

  const NumberChar = styled.label`
  color:${numberCharacters > maxDescriptionChar ? 'red' : ThemeProps.color.darkBlue}
`

  return (
    <Modal footer={null} visible={isShow} width={900} onCancel={onHide}>
      <FormContainer onSubmit={handleSubmit}>
        <ModalHeader>
          <Titles fonstSize={"2em"}>{editValues ? 'Edit Volunteer Experience' : 'Add Volunteer Experience'}</Titles>
          <LineBottomYellow />
        </ModalHeader>
        <Container>

          <Titles>Job title</Titles>
          <Input
            name="job_title"
            type="text"
            placeholder="Job title"
            value={values.job_title}
            onChange={handleChange}
          />
          {touched.job_title && errors.job_title && (
            <ErrorText>{errors.job_title}</ErrorText>
          )}
          <Divider />
          <Titles>Organization Name</Titles>
          <Input
            name="organization_name"
            type="text"
            placeholder="Organization Name"
            value={values.organization_name}
            onChange={handleChange}
          />
          {touched.organization_name && errors.organization_name && (
            <ErrorText>{errors.organization_name}</ErrorText>
          )}
          <Divider />
          <Titles>City, State</Titles>
          <Input
            name="city"
            type="text"
            className="form-control"
            placeholder="City, State"
            value={values.city}
            onChange={handleChange}
          />
          {touched.city && errors.city && (
            <ErrorText>{errors.city}</ErrorText>
          )}
          <Divider />
          <Titles>Number of hours served</Titles>
          <Input
            name="nbr_hours_served"
            type="number"
            min={0}
            className="form-control"
            placeholder="Number of hours served"
            value={values.nbr_hours_served}
            onChange={handleChange}
          />
          {touched.nbr_hours_served &&
            errors.nbr_hours_served && (
              <ErrorText>{errors.nbr_hours_served}</ErrorText>
            )}
          <Divider />
          <Row100>
            <Row50>
              <Titles>Start Date</Titles>
              <ReactDatePickerWraper
                name="start_date"
                showMonthYearPicker
                maxDate={new Date()}
                dateFormat="MM/yyyy"
                placeholderText="MM/yyyy"
                onChange={e => {
                  setFieldValue('start_date', e);
                }}
                selected={values.start_date}
              />
              {touched.start_date && errors.start_date && (
                <ErrorText>{errors.start_date}</ErrorText>
              )}
            </Row50>
            <Row50>
              <Titles>End Date</Titles>
              <ReactDatePickerWraper
                name="end_date"
                showMonthYearPicker
                maxDate={new Date()}
                dateFormat="MM/yyyy"
                placeholderText="MM/yyyy"
                onChange={e => {
                  if (
                    moment(e).isSame(moment(), 'year') &&
                    moment(e).isSame(moment(), 'month')
                  ) {
                    setFieldValue('end_date', moment().toDate());
                    return;
                  }
                  setFieldValue('end_date', e);
                  return;
                }}
                selected={values.end_date}
              />
              {touched.end_date && errors.end_date && (
                <ErrorText>{errors.end_date}</ErrorText>
              )}
            </Row50>
          </Row100>
          <Divider />
          <Titles>Description</Titles>
          <TextArea
            rows={5}
            name="description"
            onChange={(e) => {
              setNumberCharacters(e.target.value.length)
              handleChange(e)
            }}
            value={values.description}
          />
          <CharacterCounter
            maxNumberCharacters={maxDescriptionChar}
            numberCharacters={numberCharacters} />

          {touched.description && errors.description && (
            <ErrorText>{errors.description}</ErrorText>
          )}
          <Divider />
          <Row100>
            {editValues ? (
              <>
                <Button type="button" onClick={onDelete}>Delete</Button>
                <Button
                  type="submit"
                  color="#FFF"
                  backgroundColor={ThemeProps.color.darkBlue}
                >
                  Save
                </Button>
              </>
            ) :
              <>
                <Button onClick={onHide} color={ThemeProps.color.darkBlue} >Cancel</Button>
                <Button
                  type="submit"
                  color="#FFF"
                  backgroundColor={ThemeProps.color.darkBlue}
                >
                  Save
                </Button>
              </>
            }
          </Row100>
        </Container>
      </FormContainer>
    </Modal>
  );
}


const ModalHeader = styled.div`
  width: 100%;
  padding: 20px 16%;
  margin-bottom: 24px;
  background-image: url(${headerBackground});
  background-position-x: -50px;
  background-color: #fefefe;
  border-bottom: 1px solid #ddd;
`;
const Container = styled.div`
  width: 100%;
  padding: 16px 16%;
`;
const LineBottomYellow = styled.div`
  width: 100%;
  border-bottom: 3px solid ${ThemeProps.color.gabaYellow};
  margin: 12px 0;
`;
const Divider = styled.div`
  width: 100%;
  border-bottom: 3px solid #eee;
  margin: 16px 0;
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 130px;
  resize: vertical;
  border-radius: 6px;
  border-color: #ccc;
  font-size: 1.4em;
  padding: 12px 12px;
  margin: 8px 0 0 0;
  outline-color: #aaa;
  background-color: #fbfbfb;
  border: 2.5px solid #ddd;
`;
const Input = styled.input`
  width: 100%;
  margin: 8px 0 0 0;
  font-size: 1.3em;
  padding: 10px 12px;
  border-radius: 4px;
  outline-color: #aaa;
  background-color: #fbfbfb;
  border: 2.5px solid #ddd;
`;
const Titles = styled.label`
  font-size: ${p => p.fonstSize || "1.4em"};
`;
const FormContainer = styled.form`
  display:flex;
  flex-direction: column;
  width: 100%;
`;
const ReactDatePickerWraper = styled(ReactDatePicker)`
  width: 100%;
  margin: 8px 0;
  font-size: 1.3em;
  padding: 8px 12px;
  border: 1px solid #ccc;
  background-color: #fbfbfb;
  outline-color: #aaa;
  border-radius: 4px;
`;
const Row100 = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;
const Row50 = styled.div`
  width: 49%;
  display: flex;
  flex-direction: column;
`;
const Button = styled.button`
  width: 49%;
  padding: 14px 20px;
  font-size: 1.3em;
  margin-top: 8px;
  border-radius: 6px;
  background-color: ${p => p.backgroundColor};
  color: ${p => p.color};
  border: none;
  outline: none;
`;
const ErrorText = styled.p`
  color: #F00;
  margin: 8px 0;
`;